import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Clock, Truck, AlertCircle, CheckCircle, XCircle, TrendingUp, Package, User, FileText } from 'lucide-react';
import { useToast, LoadingState, EmptyState, FilterSection } from '../components/ui';
import api from '../services/api';

const TruckQueue = () => {
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();
  const [selectedQueueEntry, setSelectedQueueEntry] = useState(null);
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [newPriority, setNewPriority] = useState(3);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    priority: 'all'
  });

  // Fetch queue data
  const { data: queueData, isLoading } = useQuery({
    queryKey: ['truck-queue'],
    queryFn: async () => {
      const response = await api.get('/warehouse/queue/');
      return response.data.results || response.data;
    },
    refetchInterval: 10000 // Refresh every 10 seconds
  });

  // Fetch waiting trucks specifically
  const { data: waitingData } = useQuery({
    queryKey: ['truck-queue-waiting'],
    queryFn: async () => {
      const response = await api.get('/warehouse/queue/waiting/');
      return response.data;
    },
    refetchInterval: 10000
  });

  // Cancel queue entry mutation
  const cancelMutation = useMutation({
    mutationFn: async (queueId) => {
      const response = await api.post(`/warehouse/queue/${queueId}/cancel/`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['truck-queue']);
      queryClient.invalidateQueries(['truck-queue-waiting']);
      success('Queue entry cancelled successfully');
    },
    onError: (err) => {
      showError(err.response?.data?.error || 'Failed to cancel queue entry');
    }
  });

  // Change priority mutation
  const changePriorityMutation = useMutation({
    mutationFn: async ({ queueId, priority }) => {
      const response = await api.post(`/warehouse/queue/${queueId}/change_priority/`, {
        priority
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['truck-queue']);
      queryClient.invalidateQueries(['truck-queue-waiting']);
      success('Priority changed successfully');
      setShowPriorityModal(false);
      setSelectedQueueEntry(null);
    },
    onError: (err) => {
      showError(err.response?.data?.error || 'Failed to change priority');
    }
  });

  const handleCancelQueue = (queueEntry) => {
    if (window.confirm(`Are you sure you want to cancel queue entry for truck ${queueEntry.truck_number}?`)) {
      cancelMutation.mutate(queueEntry.id);
    }
  };

  const handleChangePriority = () => {
    if (selectedQueueEntry) {
      changePriorityMutation.mutate({
        queueId: selectedQueueEntry.id,
        priority: newPriority
      });
    }
  };

  const getPriorityBadge = (priority, priorityDisplay) => {
    const colors = {
      1: 'bg-red-100 text-red-800 border-red-300',
      2: 'bg-orange-100 text-orange-800 border-orange-300',
      3: 'bg-blue-100 text-blue-800 border-blue-300',
      4: 'bg-gray-100 text-gray-800 border-gray-300'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-semibold border-2 ${colors[priority]}`}>
        P{priority} - {priorityDisplay}
      </span>
    );
  };

  const getStatusBadge = (status, statusDisplay) => {
    const colors = {
      'WAITING': 'bg-yellow-100 text-yellow-800',
      'ASSIGNED': 'bg-green-100 text-green-800',
      'CANCELLED': 'bg-gray-100 text-gray-800',
      'TIMEOUT': 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${colors[status]}`}>
        {statusDisplay}
      </span>
    );
  };

  const formatWaitTime = (hours) => {
    if (hours < 1) {
      return `${Math.round(hours * 60)} minutes`;
    }
    return `${hours.toFixed(1)} hours`;
  };

  const waitingTrucks = queueData?.filter(q => q.status === 'WAITING') || [];
  const otherTrucks = queueData?.filter(q => q.status !== 'WAITING') || [];

  // Filter logic
  const filteredWaitingTrucks = waitingTrucks.filter(truck => {
    const matchesSearch =
      truck.truck_number?.toLowerCase().includes(filters.search.toLowerCase()) ||
      truck.supplier_name?.toLowerCase().includes(filters.search.toLowerCase()) ||
      truck.driver_name?.toLowerCase().includes(filters.search.toLowerCase());

    const matchesPriority = filters.priority === 'all' || truck.priority === parseInt(filters.priority);

    return matchesSearch && matchesPriority;
  });

  // Filter options for FilterSection
  const filterOptions = [
    {
      name: 'priority',
      label: 'Priority',
      value: filters.priority,
      options: [
        { value: '1', label: 'Urgent (P1)' },
        { value: '2', label: 'High (P2)' },
        { value: '3', label: 'Normal (P3)' },
        { value: '4', label: 'Low (P4)' }
      ]
    }
  ];

  if (isLoading) return <LoadingState />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <Clock className="w-8 h-8 mr-3 text-orange-500" />
          Truck Queue Management
        </h1>
        <p className="text-gray-600 mt-2">
          Manage trucks waiting for dock assignment
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card bg-gradient-to-br from-orange-500 to-orange-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm">Waiting</p>
              <p className="text-3xl font-bold text-white mt-1">{waitingData?.count || 0}</p>
            </div>
            <div className="bg-white bg-opacity-20 p-3 rounded-lg">
              <Clock className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-red-500 to-red-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-100 text-sm">Urgent Priority</p>
              <p className="text-3xl font-bold text-white mt-1">
                {waitingTrucks.filter(t => t.priority === 1).length}
              </p>
            </div>
            <div className="bg-white bg-opacity-20 p-3 rounded-lg">
              <AlertCircle className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-yellow-500 to-yellow-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-100 text-sm">Timeouts</p>
              <p className="text-3xl font-bold text-white mt-1">
                {waitingTrucks.filter(t => t.is_timeout).length}
              </p>
            </div>
            <div className="bg-white bg-opacity-20 p-3 rounded-lg">
              <AlertCircle className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-green-500 to-green-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Assigned Today</p>
              <p className="text-3xl font-bold text-white mt-1">
                {otherTrucks.filter(t => t.status === 'ASSIGNED').length}
              </p>
            </div>
            <div className="bg-white bg-opacity-20 p-3 rounded-lg">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <FilterSection
        filters={filterOptions}
        onFilterChange={(name, value) => setFilters(prev => ({ ...prev, [name]: value }))}
        onClearFilters={() => setFilters({ search: '', status: 'all', priority: 'all' })}
        searchValue={filters.search}
        searchPlaceholder="Search by truck number, supplier, or driver..."
      />

      {/* Waiting Queue */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <TrendingUp className="w-6 h-6 mr-2 text-orange-500" />
            Currently Waiting ({filteredWaitingTrucks.length})
          </h2>
        </div>

        {filteredWaitingTrucks.length === 0 ? (
          <EmptyState
            icon={CheckCircle}
            message="No trucks in queue"
            description={filters.search || filters.priority !== 'all' ? "Try adjusting your filters" : "All trucks have been assigned to docks"}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredWaitingTrucks.map((queueEntry) => (
              <div
                key={queueEntry.id}
                className={`card border-2 cursor-pointer hover:shadow-lg transition-shadow animate-fade-in ${
                  queueEntry.is_timeout ? 'border-red-400 bg-red-50' : 'border-gray-200'
                }`}
                onClick={() => {
                  setSelectedQueueEntry(queueEntry);
                  setShowDetailsModal(true);
                }}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <Truck className="w-5 h-5 text-gray-600" />
                      <h3 className="text-xl font-bold text-gray-900">{queueEntry.truck_number}</h3>
                    </div>
                    {queueEntry.supplier_name && (
                      <p className="text-sm text-gray-600 mt-1">{queueEntry.supplier_name}</p>
                    )}
                  </div>
                  {queueEntry.position && (
                    <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs font-bold">
                      #{queueEntry.position}
                    </span>
                  )}
                </div>

                {queueEntry.is_timeout && (
                  <div className="mb-3 p-2 bg-red-100 border border-red-300 rounded-lg flex items-center">
                    <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
                    <span className="text-xs font-bold text-red-800">TIMEOUT WARNING</span>
                  </div>
                )}

                <div className="space-y-2 mb-4">
                  {getPriorityBadge(queueEntry.priority, queueEntry.priority_display)}

                  <div className="flex items-center text-sm text-gray-600">
                    <Clock className="w-4 h-4 mr-2 text-orange-500" />
                    <span className="font-semibold">{formatWaitTime(queueEntry.wait_time_hours)}</span>
                    <span className="mx-2 text-gray-400">/</span>
                    <span>{queueEntry.max_wait_time_hours}h max</span>
                  </div>

                  {queueEntry.driver_name && (
                    <div className="flex items-center text-sm text-gray-600">
                      <User className="w-4 h-4 mr-2 text-blue-500" />
                      <span>{queueEntry.driver_name}</span>
                    </div>
                  )}
                </div>

                <div className="flex space-x-2 pt-3 border-t border-gray-200">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedQueueEntry(queueEntry);
                      setNewPriority(queueEntry.priority);
                      setShowPriorityModal(true);
                    }}
                    className="flex-1 px-3 py-1.5 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm font-medium"
                  >
                    Priority
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCancelQueue(queueEntry);
                    }}
                    className="flex-1 px-3 py-1.5 bg-red-500 text-white rounded hover:bg-red-600 text-sm font-medium"
                    disabled={cancelMutation.isLoading}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* History */}
      {otherTrucks.length > 0 && (
        <div className="bg-white rounded-lg shadow-md">
          <div className="bg-gradient-to-r from-gray-600 to-gray-700 text-white px-6 py-4 rounded-t-lg">
            <h2 className="text-xl font-bold flex items-center">
              <Package className="w-6 h-6 mr-2" />
              Queue History ({otherTrucks.length})
            </h2>
          </div>

          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-2 px-4">Truck</th>
                    <th className="text-left py-2 px-4">Supplier</th>
                    <th className="text-left py-2 px-4">Priority</th>
                    <th className="text-left py-2 px-4">Status</th>
                    <th className="text-left py-2 px-4">Wait Time</th>
                    <th className="text-left py-2 px-4">Added</th>
                  </tr>
                </thead>
                <tbody>
                  {otherTrucks.map((queueEntry) => (
                    <tr key={queueEntry.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 font-semibold">{queueEntry.truck_number}</td>
                      <td className="py-3 px-4">{queueEntry.supplier_name || 'N/A'}</td>
                      <td className="py-3 px-4">
                        {getPriorityBadge(queueEntry.priority, queueEntry.priority_display)}
                      </td>
                      <td className="py-3 px-4">
                        {getStatusBadge(queueEntry.status, queueEntry.status_display)}
                      </td>
                      <td className="py-3 px-4">{formatWaitTime(queueEntry.wait_time_hours)}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {new Date(queueEntry.added_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Truck Details Modal */}
      {showDetailsModal && selectedQueueEntry && !showPriorityModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-scale-in">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{selectedQueueEntry.truck_number}</h2>
                <p className="text-gray-600 mt-1">Queue Details</p>
              </div>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedQueueEntry(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            {/* Status and Priority */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-sm text-gray-600 mb-2">Status</p>
                {getStatusBadge(selectedQueueEntry.status, selectedQueueEntry.status_display)}
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-2">Priority</p>
                {getPriorityBadge(selectedQueueEntry.priority, selectedQueueEntry.priority_display)}
              </div>
            </div>

            {/* Timeout Warning */}
            {selectedQueueEntry.is_timeout && (
              <div className="mb-6 p-4 bg-red-100 border-2 border-red-300 rounded-lg flex items-center">
                <AlertCircle className="w-6 h-6 text-red-600 mr-3" />
                <div>
                  <p className="font-bold text-red-800">Timeout Warning</p>
                  <p className="text-sm text-red-700">This truck has exceeded the maximum wait time</p>
                </div>
              </div>
            )}

            {/* Queue Information */}
            <div className="mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <Clock className="w-5 h-5 mr-2 text-orange-500" />
                Queue Information
              </h3>
              <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                {selectedQueueEntry.position && (
                  <div>
                    <p className="text-sm text-gray-600">Position in Queue</p>
                    <p className="text-lg font-bold text-purple-600">#{selectedQueueEntry.position}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-600">Wait Time</p>
                  <p className="text-lg font-bold text-orange-600">
                    {formatWaitTime(selectedQueueEntry.wait_time_hours)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Max Wait Time</p>
                  <p className="text-lg font-bold text-gray-900">
                    {selectedQueueEntry.max_wait_time_hours} hours
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Added At</p>
                  <p className="text-lg font-bold text-gray-900">
                    {new Date(selectedQueueEntry.added_at).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Truck & Supplier Details */}
            <div className="mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <Truck className="w-5 h-5 mr-2 text-blue-500" />
                Truck & Supplier Details
              </h3>
              <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                {selectedQueueEntry.supplier_name && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Supplier</span>
                    <span className="font-semibold text-gray-900">{selectedQueueEntry.supplier_name}</span>
                  </div>
                )}
                {selectedQueueEntry.driver_name && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Driver</span>
                    <span className="font-semibold text-gray-900">{selectedQueueEntry.driver_name}</span>
                  </div>
                )}
                {selectedQueueEntry.driver_mobile && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Driver Mobile</span>
                    <span className="font-semibold text-gray-900">{selectedQueueEntry.driver_mobile}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Queue Reason */}
            {selectedQueueEntry.queue_reason && (
              <div className="mb-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-blue-500" />
                  Queue Reason
                </h3>
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                  <p className="text-gray-900">{selectedQueueEntry.queue_reason}</p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex space-x-3 pt-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setNewPriority(selectedQueueEntry.priority);
                  setShowDetailsModal(false);
                  setShowPriorityModal(true);
                }}
                className="flex-1 bg-blue-500 text-white px-4 py-2.5 rounded-lg hover:bg-blue-600 font-medium flex items-center justify-center"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Change Priority
              </button>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  handleCancelQueue(selectedQueueEntry);
                }}
                className="flex-1 bg-red-500 text-white px-4 py-2.5 rounded-lg hover:bg-red-600 font-medium flex items-center justify-center"
                disabled={cancelMutation.isLoading}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Cancel Queue
              </button>
            </div>

            <button
              onClick={() => {
                setShowDetailsModal(false);
                setSelectedQueueEntry(null);
              }}
              className="w-full mt-3 bg-gray-200 text-gray-800 px-4 py-2.5 rounded-lg hover:bg-gray-300 font-medium"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Priority Change Modal */}
      {showPriorityModal && selectedQueueEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold mb-4">Change Priority</h2>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">Truck Number</p>
              <p className="text-lg font-bold">{selectedQueueEntry.truck_number}</p>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">Current Priority</p>
              {getPriorityBadge(selectedQueueEntry.priority, selectedQueueEntry.priority_display)}
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Priority
              </label>
              <select
                value={newPriority}
                onChange={(e) => setNewPriority(parseInt(e.target.value))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value={1}>1 - Urgent</option>
                <option value={2}>2 - High</option>
                <option value={3}>3 - Normal</option>
                <option value={4}>4 - Low</option>
              </select>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleChangePriority}
                disabled={changePriorityMutation.isLoading}
                className="flex-1 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 disabled:opacity-50 font-medium"
              >
                {changePriorityMutation.isLoading ? 'Changing...' : 'Change Priority'}
              </button>
              <button
                onClick={() => {
                  setShowPriorityModal(false);
                  setSelectedQueueEntry(null);
                }}
                className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TruckQueue;
